/* eslint-disable no-nested-ternary */
import block from 'propmods';
import React from 'react';
import { useI18N } from '@yandex-int/i18n/lib/react';

import * as keyset from 'i18n/SkillsIndex';
import { moment } from '../../lib/moment';
import { routes } from '../../lib/routes';
import { DraftStatusType } from '../../model/draft';
import { OperationType } from '../../model/operation';
import { Channel } from '../../model/skill';
import Link from '../Link/Link';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { DraftStatusComponent } from '../statuses/DraftStatus';
import { OperationTypeComponent } from '../statuses/OperationType';

import DialogIcon from './dialog.svg';

import './SkillCard.scss';

export type SkillCardTheme = 'normal' | 'danger' | 'draft' | 'create' | 'forbidden';

export interface SkillCardProps<TChannel extends Channel> {
    id?: string;
    logo?: string;
    theme?: SkillCardTheme;
    title?: string;
    status?: { model: 'draft'; status: DraftStatusType<TChannel> } | { model: 'operation'; status: OperationType };
    updatedAt?: string;
    onClick?: () => void;
    channel: TChannel;
    rtl?: boolean;
}

const b = block('SkillCard');

export default function SkillCard<TChannel extends Channel>(props: SkillCardProps<TChannel>) {
    const t = useI18N(keyset);
    const iconProps = props.logo ? { backgroundImage: `url(${props.logo})` } : { border: '1px solid #D7D7D7' };

    if (props.theme === 'create') {
        return (
            <div {...b(props)} onClick={props.onClick}>
                <DialogIcon />
                {t('create-dialog')}
            </div>
        );
    }

    if (props.theme === 'forbidden') {
        return (
            <div {...b(props)}>
                <span>
                    {t('limit-exceeded', {
                        link: (
                            <Link to={routes.documentation.aliceSkills.considerations}>{t('limit-exceeded__doc')}</Link>
                        ),
                    })}
                </span>
            </div>
        );
    }

    return (
        <ErrorBoundary>
            <Link to={routes.app.skill(props.id)}>
                <div {...b(props)}>
                    <div {...b('title', { rtl: props.rtl })}>{props.title}</div>
                    <div {...b('icon', { channel: props.channel })} style={iconProps} />
                    <div {...b('footer')}>
                        <span {...b('status')}>
                            {props.status ? (
                                props.status.model === 'draft' ? (
                                    <DraftStatusComponent status={props.status.status} channel={props.channel} />
                                ) : (
                                    <OperationTypeComponent
                                        type={props.status.status}
                                        channel={props.channel}
                                        useShortText
                                    />
                                )
                            ) : null}
                        </span>
                        <span {...b('date')}>{moment(props.updatedAt).fromNow()}</span>
                    </div>
                </div>
            </Link>
        </ErrorBoundary>
    );
}
